import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import { NavItem } from './nav-item';
import { NavUl, NavLi } from '../styles';
import { navSectionClasses } from '../classes';
import type { NavListProps, NavSubListProps } from '../types';
import { usePathname } from 'next/navigation';
import { isExternalLink } from '@/lib/utils';
import { paper } from '../../theme/styles';
import { useActiveLink } from '../../routes/use-active-link';

// ----------------------------------------------------------------------

export function NavList({
  data,
  depth,
  render,
  cssVars,
  slotProps,
  enabledRootRedirect,
  siblingPaths,
}: NavListProps) {
  const theme = useTheme();

  const pathname = usePathname();

  const navItemRef = useRef<HTMLButtonElement | null>(null);

  const active = useActiveLink(data.path, !!data.children);
  // A sibling nav item "claims" the current route more specifically than this
  // one when its path is a longer prefix of the pathname (e.g. Informed
  // Delivery's '/campaigns/informed-delivery' vs. Direct Mail's '/campaigns'
  // both prefix-match '/campaigns/informed-delivery/details') — in that case
  // this item shouldn't also render as active.
  const siblingMoreSpecificMatch = !data.children && (siblingPaths?.some((p) => {
    if (p === data.path || !p || p === '#' || p.startsWith('http')) return false;
    const siblingMatches = pathname === p || (pathname || '').startsWith(p + '/');
    return siblingMatches && p.length > (data.path || '').length;
  }) ?? false);
  const effectiveActive = active && !siblingMoreSpecificMatch;

  const [openMenu, setOpenMenu] = useState(false);

  useEffect(() => {
    if (openMenu) {
      handleCloseMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpenMenu = useCallback(() => {
    if (data.children) {
      setOpenMenu(true);
    }
  }, [data.children]);

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(false);
  }, []);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      setOpenMenu(!openMenu);
    }
  }, [data.children, openMenu]);

  const renderNavItem = (
    <NavItem
      ref={navItemRef}
      render={render}
      // slots
      title={data.title}
      path={data.path}
      icon={data.icon}
      info={data.info}
      caption={data.caption}
      // state
      depth={depth}
      active={effectiveActive}
      disabled={data.disabled}
      hasChild={!!data.children}
      open={data.children ? openMenu : undefined}
      externalLink={isExternalLink(data.path)}
      enabledRootRedirect={enabledRootRedirect}
      // styles
      slotProps={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
      // actions
      onToggleMenu={handleToggleMenu}
      onClick={data.onClick}
    />
  );

  // Hidden item by role
  if (data.roles && slotProps?.currentRole) {
    if (!data?.roles?.includes(slotProps?.currentRole)) {
      return null;
    }
  }

  // Has children
  if (data.children) {
    if (depth === 1) {
      return (
        <NavLi disabled={data.disabled}>
          {renderNavItem}

          <Popover
            disableScrollLock
            open={openMenu}
            onClose={handleCloseMenu}
            anchorEl={navItemRef.current}
            anchorOrigin={
              depth === 1
                ? { vertical: 'bottom', horizontal: 'left' }
                : { vertical: 'center', horizontal: 'right' }
            }
            transformOrigin={
              depth === 1
                ? { vertical: 'top', horizontal: 'left' }
                : { vertical: 'center', horizontal: 'left' }
            }
            slotProps={{
              paper: {
                sx: {
                  px: 0.75,
                  boxShadow: 'none',
                  overflow: 'unset',
                  backdropFilter: 'none',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  ...(depth === 1 && { ml: -0.75 }),
                  ...(openMenu && { pointerEvents: 'auto' }),
                },
              },
            }}
            sx={{ ...cssVars, pointerEvents: 'none' }}
          >
            <Paper
              className={navSectionClasses.paper}
              sx={{
                minWidth: 210,
                p: 1.25,
                ...paper({ theme, dropdown: true }),
                backgroundImage: 'none',
                border: '1px solid var(--line-strong, rgba(255, 255, 255, 0.12))',
                boxShadow: 'var(--shadow-3, 0px 12px 32px rgba(0, 0, 0, 0.36))',
                borderRadius: '12px',
                ...slotProps?.paper,
              }}
            >
              {data.title && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      px: 1,
                      pt: 0.5,
                      pb: 0.5,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      letterSpacing: '-0.01em',
                      color: 'text.primary',
                    }}
                  >
                    {data.title}
                  </Typography>
                  <Divider sx={{ my: 1, borderColor: 'var(--line-strong, rgba(255, 255, 255, 0.12))' }} />
                </>
              )}

              <NavSubList
                data={data.children}
                depth={depth}
                render={render}
                cssVars={cssVars}
                slotProps={slotProps}
                enabledRootRedirect={enabledRootRedirect}
              />
            </Paper>
          </Popover>
        </NavLi>
      );
    }

    // Render depth > 1 sub-children inline within the SAME popover
    return (
      <NavLi disabled={data.disabled}>
        {renderNavItem}
        <Box sx={{ pl: 2, pt: 0.25, pb: 0.5 }}>
          <NavSubList
            data={data.children}
            depth={depth}
            render={render}
            cssVars={cssVars}
            slotProps={slotProps}
            enabledRootRedirect={enabledRootRedirect}
          />
        </Box>
      </NavLi>
    );
  }

  // Default
  return <NavLi disabled={data.disabled}>{renderNavItem}</NavLi>;
}

// ----------------------------------------------------------------------

function NavSubList({
  data,
  depth,
  render,
  cssVars,
  slotProps,
  enabledRootRedirect,
}: NavSubListProps) {
  const siblingPaths = data.map((item) => item.path);
  return (
    <NavUl sx={{ gap: 0.5 }}>
      {data.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          cssVars={cssVars}
          slotProps={slotProps}
          enabledRootRedirect={enabledRootRedirect}
          siblingPaths={siblingPaths}
        />
      ))}
    </NavUl>
  );
}
